// src/pages/TripsPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import { tripService, UPLOADS_BASE_URL } from '../services/api';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import TripFilters from '../components/TripFilters';
import Pagination from '../components/Pagination';
import SEO from '../components/SEO';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';
import './HomePage.css';

const TripsPage = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    certificationLevel: '',
    status: '',
    priceRange: '',
    availableOnly: false
  });
  const [sortBy, setSortBy] = useState('date-asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9); // 9 trips per page (3x3 grid)

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        setLoading(true);
        const response = await tripService.getAllTrips();
        setTrips(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to load trips. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrips();
  }, []);

  // Filter trips based on current filters
  const filteredTrips = useMemo(() => {
    let result = trips.filter(trip => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesName = trip.name.toLowerCase().includes(searchLower);
        const matchesLocation = trip.location.toLowerCase().includes(searchLower);
        if (!matchesName && !matchesLocation) return false;
      }

      // Certification level filter
      if (filters.certificationLevel && trip.minimumCertification !== filters.certificationLevel) {
        return false;
      }

      // Status filter
      if (filters.status && trip.status !== filters.status) {
        return false;
      }

      // Price range filter
      if (filters.priceRange && trip.price) {
        const [min, max] = filters.priceRange.split('-').map(v => v.replace('+', ''));
        const price = trip.price;
        if (max) {
          if (price < parseInt(min) || price > parseInt(max)) return false;
        } else {
          if (price < parseInt(min)) return false;
        }
      }

      // Available spots filter
      if (filters.availableOnly) {
        const spotsLeft = trip.capacity - (trip.approvedCount || 0);
        if (spotsLeft <= 0) return false;
      }

      return true;
    });

    // Sort trips
    result.sort((a, b) => {
      switch (sortBy) {
        case 'date-asc':
          return new Date(a.startDate) - new Date(b.startDate);
        case 'date-desc':
          return new Date(b.startDate) - new Date(a.startDate);
        case 'price-asc':
          return (a.price || 0) - (b.price || 0);
        case 'price-desc':
          return (b.price || 0) - (a.price || 0);
        case 'spots-desc':
          return (b.capacity - (b.approvedCount || 0)) - (a.capacity - (a.approvedCount || 0));
        case 'newest':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        default:
          return 0;
      }
    });

    return result;
  }, [trips, filters, sortBy]);

  const handleResetFilters = () => {
    setFilters({
      search: '',
      certificationLevel: '',
      status: '',
      priceRange: '',
      availableOnly: false
    });
    setCurrentPage(1);
  };

  // Reset to page 1 when filters or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy]);

  // Paginated trips
  const paginatedTrips = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredTrips.slice(startIndex, endIndex);
  }, [filteredTrips, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredTrips.length / itemsPerPage);

  if (loading) {
    return (
      <Container>
        <LoadingSpinner fullPage message="Loading diving trips..." />
      </Container>
    );
  }

  return (
    <Container>
      <SEO 
        title="Browse Scuba Diving Trips - Worldwide Dive Vacations | DiveConnect"
        description="Discover and book amazing scuba diving trips worldwide. From Maldives liveaboards to Red Sea adventures, find your perfect dive vacation with expert guides and small groups."
        keywords="scuba diving trips, dive vacations, liveaboard diving, dive packages, scuba travel, diving holidays, dive tours, underwater adventure, diving destinations"
        url="https://dive-connect.com/trips"
      />
      
      <h1 className="mb-4">Diving Trips</h1>
      
      {error && (
        <ErrorMessage 
          title="Failed to Load Trips"
          message={error}
          onRetry={() => window.location.reload()}
          showDetails={true}
        />
      )}
      
      <TripFilters 
        filters={filters} 
        setFilters={setFilters}
        onReset={handleResetFilters}
      />
      
      {trips.length === 0 ? (
        <EmptyState 
          type="default"
          title="No Trips Available"
          message="We're currently updating our trip offerings. Check back soon for amazing diving adventures!"
          actionLabel="Explore Blog"
          onAction={() => window.location.href = '/blog'}
        />
      ) : filteredTrips.length === 0 ? (
        <EmptyState 
          type="search"
          title="No Trips Match Your Filters"
          message="Try adjusting your search criteria, price range, or difficulty level to find more trips."
          actionLabel="Reset Filters"
          onAction={handleResetFilters}
        />
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="text-muted">
              Showing {filteredTrips.length} of {trips.length} trip{trips.length !== 1 ? 's' : ''}
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 me-2">Sort by:</label>
              <Form.Select 
                style={{ width: 'auto' }} 
                size="sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date-asc">Date (Soonest First)</option>
                <option value="date-desc">Date (Latest First)</option>
                <option value="price-asc">Price (Low to High)</option>
                <option value="price-desc">Price (High to Low)</option>
                <option value="spots-desc">Availability (Most Spots)</option>
                <option value="newest">Newest Added</option>
              </Form.Select>
            </div>
          </div>
          <Row>
            {paginatedTrips.map((trip) => (
              <Col key={trip._id} md={4} className="mb-4">
              <Card className="trip-card h-100">
              <div className="trip-card-img">
                <img 
                  src={(() => {
                    // Use first image from gallery, or fall back to single image, or default
                    const displayImage = trip.images && trip.images.length > 0 
                      ? trip.images[0] 
                      : trip.image || 'default-trip.jpg';
                    
                    return displayImage !== 'default-trip.jpg'
                      ? `${UPLOADS_BASE_URL}/uploads/${displayImage}`
                      : '/images/default-trip.jpg';
                  })()} 
                  alt={trip.name}
                  style={{width: '100%', height: '100%', objectFit: 'cover'}}
                  onError={(e) => {
                    console.error('Image failed to load:', e.target.src);
                    e.target.src = '/images/default-trip.jpg';
                  }}
                />
                <div className="trip-card-badge">{trip.status}</div>
                {trip.images && trip.images.length > 1 && (
                  <div className="trip-card-gallery-badge">
                    <i className="bi bi-images"></i> {trip.images.length}
                  </div>
                )}
              </div>
                <Card.Body>
                  <Card.Title>{trip.name}</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">
                    {new Date(trip.startDate).toLocaleDateString()} - 
                    {new Date(trip.endDate).toLocaleDateString()}
                  </Card.Subtitle>
                  <div className="trip-details">
                    <div className="trip-detail">
                      <i className="bi bi-geo-alt-fill"></i> {trip.location}
                    </div>
                    <div className="trip-detail">
                      <i className="bi bi-people-fill"></i> {trip.approvedCount || 0}/{trip.capacity} Spots
                    </div>
                    <div className="trip-detail">
                      <i className="bi bi-award"></i> {trip.minimumCertification} or higher
                    </div>
                    {trip.price && (
                      <div className="trip-detail">
                        <i className="bi bi-currency-euro"></i> <strong>{trip.price} {trip.currency || 'EUR'}</strong>
                      </div>
                    )}
                  </div>
                  <Link to={`/trips/${trip._id}`}>
                    <Button variant="primary" className="mt-3 w-100">
                      View Details
                    </Button>
                  </Link>
                </Card.Body>
              </Card>
              </Col>
            ))}
          </Row>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredTrips.length}
            itemsPerPage={itemsPerPage}
          />
        </>
      )}
    </Container>
  );
};

export default TripsPage;